import type { AlertColor } from "@mui/material/Alert/Alert";
import { AxiosError } from "axios";
import React, { type Dispatch } from "react";
import { Route, Routes, useParams } from "react-router-dom";

import type QueryClient from "../../api/query/queryClient";
import useDevMode from "../../utils/devmode";
import PageNotFound from "../PageNotFound";
import type {
  PatientsStore,
  PatientsStoreAction,
} from "../PatientsDashboard/store";
import {
  initialPaymentPlanStore,
  type PaymentPlanStore,
  type PaymentPlanStoreAction,
  paymentPlanStoreReducer,
} from "./store";
import CreatePaymentPlan from "./views/Create";
import { Success } from "./views/create/Success";

interface PaymentPlanProps {
  patientStore: PatientsStore;
  updatePatientStore: Dispatch<PatientsStoreAction>;
  store: PaymentPlanStore;
  updateStore: Dispatch<PaymentPlanStoreAction>;
  queryClient: QueryClient;
  updateSnackBar: (snackbar: { severity: AlertColor; message: string }) => void;
}

export { initialPaymentPlanStore, paymentPlanStoreReducer };

export default function PatientsPaymentDashboard({
  patientStore,
  updatePatientStore,
  store,
  updateStore,
  queryClient,
  updateSnackBar,
}: PaymentPlanProps) {
  const { patientId } = useParams();
  const devMode = useDevMode();

  const loadPatientAndClaims = async () => {
    if (!patientId) return;
    try {
      const patientClaims = await queryClient.getPatientClaims(patientId);
      updatePatientStore({ type: "SET_PATIENT", patient: patientClaims });
      const claimIds = (patientClaims?.Claim ?? []).map((c) => c.wieldyId);
      const claims = claimIds.length
        ? (await queryClient.getClaimsProceduresAndPatient(claimIds)).data
        : [];
      updatePatientStore({
        type: "SET_CLAIMS",
        claims,
      });
    } catch (error: unknown) {
      updatePatientStore({ type: "SET_PATIENT", patient: undefined });
      if (error instanceof AxiosError && error.response?.data) {
        const responseBody = error.response.data;
        updateSnackBar({
          severity: "error",
          message:
            (typeof responseBody.detail === "string"
              ? responseBody.detail
              : responseBody.detail?.[0]?.msg) || "Failed to load the patient.",
        });
      } else {
        updateSnackBar({
          severity: "error",
          message: "Failed to load the patient.",
        });
      }
    }
  };

  // biome-ignore lint/correctness/useExhaustiveDependencies: adding loadPatientAndClaims would change when this runs
  React.useEffect(() => {
    if (!patientStore.patient) {
      loadPatientAndClaims();
    }
  }, [patientStore.patient]);

  if (!patientId || !devMode) {
    // Add 404 or redirect here
    return <PageNotFound />;
  }

  return (
    <Routes>
      <Route
        path="/new/*"
        element={
          <CreatePaymentPlan
            patientId={patientId}
            patientStore={patientStore}
            store={store}
            updateStore={updateStore}
            queryClient={queryClient}
            updateSnackBar={updateSnackBar}
          />
        }
      />
      <Route
        path="/:paymentPlanId/success"
        element={
          <Success
            patientId={patientId}
            queryClient={queryClient}
            updateSnackBar={updateSnackBar}
          />
        }
      />
    </Routes>
  );
}
